import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@shared/contexts/AuthContext";
import axios from "axios";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// F-A min password length (matches backend ChangePasswordRequest min_length=8
// and the F-A password policy: ≥8 chars, no max, no complexity rules).
const MIN_PASSWORD_LENGTH = 8;

const ChangePassword = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  // F-A first-login gate: StaffLogin redirects here with ?firstLogin=1 when
  // the login response carried mustChangePassword. In that mode the screen
  // becomes a one-time setup step the user cannot skip.
  const firstLogin = searchParams.get("firstLogin") === "1";
  const [formData, setFormData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (formData.new_password.length < MIN_PASSWORD_LENGTH) {
      toast.error(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
      );
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/staff/change-password`,
        {
          old_password: formData.old_password,
          new_password: formData.new_password,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        firstLogin
          ? "Password set — welcome!"
          : "Password changed successfully",
      );
      navigate("/staff/exhibitions");
    } catch (error) {
      const detail = axios.isAxiosError(error)
        ? error.response?.data?.detail
        : undefined;
      toast.error(detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="change-password-page"
    >
      <div className="bg-white border-b border-border">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            {!firstLogin && (
              <Button
                onClick={() => navigate("/staff/exhibitions")}
                variant="ghost"
                size="sm"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
            <h1 className="text-2xl font-heading font-bold">
              {firstLogin ? "One-time setup" : "Change Password"}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {firstLogin && (
          <p
            className="mb-6 text-sm text-muted-foreground"
            data-testid="first-login-notice"
          >
            Your administrator created this account with a temporary password.
            Please set a new password before you continue.
          </p>
        )}
        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-white p-6 border border-border rounded-lg"
        >
          <div className="space-y-2">
            <Label htmlFor="old_password">
              {firstLogin ? "Temporary password" : "Current Password"}
            </Label>
            <Input
              id="old_password"
              type="password"
              value={formData.old_password}
              onChange={(e) =>
                setFormData({ ...formData, old_password: e.target.value })
              }
              required
              data-testid="old-password-input"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new_password">New Password</Label>
            <Input
              id="new_password"
              type="password"
              value={formData.new_password}
              onChange={(e) =>
                setFormData({ ...formData, new_password: e.target.value })
              }
              required
              data-testid="new-password-input"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm New Password</Label>
            <Input
              id="confirm_password"
              type="password"
              value={formData.confirm_password}
              onChange={(e) =>
                setFormData({ ...formData, confirm_password: e.target.value })
              }
              required
              data-testid="confirm-password-input"
              className="h-12"
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-change-password-button"
            >
              {loading
                ? "Saving..."
                : firstLogin
                  ? "Set password"
                  : "Change Password"}
            </Button>
            {!firstLogin && (
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/staff/exhibitions")}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
